import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlusOutlined } from "@ant-design/icons";
import { App, Collapse, Empty, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import BrandButton, { brandModalConfirmProps } from "@/components/BrandButton";
import {
  PRODUCT_LIMIT,
  ProductApiCode,
  ProductApiError,
  ProductStatus,
  cancelScheduleProduct,
  deleteProduct,
  duplicateProduct,
  listNowProduct,
  listProducts,
  publishProduct,
  unlistProduct,
  relistProduct,
  type BillingType,
  type SubscriptionProduct,
} from "@/api/products";
import {
  BILLING_I18N,
  PRODUCT_STATUS_I18N,
  PUBLISH_ISSUE_KEY,
  displayName,
  groupProducts,
  periodPhraseKey,
  periodSummary,
  utcWall,
} from "./rules";
import {
  ConfirmApi,
  ConfirmCancel,
  LIST_ACTION_LABEL,
  ListAction,
  ListCommandType,
  actionsForStatus,
  resolveListAction,
  type ConfirmDialog,
} from "./statusActions";
import ProductEdit from "./Edit";
import ArrowCollapseIcon from "@/assets/arrow_collspse.svg?react";
import Button from "@/components/Button";
import styles from "./index.module.scss";

const RUN_API = {
  [ConfirmApi.Publish]: publishProduct,
  [ConfirmApi.ListNow]: listNowProduct,
  [ConfirmApi.Cancel]: cancelScheduleProduct,
  [ConfirmApi.Unlist]: unlistProduct,
  [ConfirmApi.Relist]: relistProduct,
  [ConfirmApi.Delete]: deleteProduct,
} as const;

function Products() {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const { app_id = "" } = useParams();

  const [rows, setRows] = useState<SubscriptionProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<
    { kind: "create" } | { kind: "edit"; id: string } | null
  >(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const atLimit = rows.length >= PRODUCT_LIMIT;

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      setRows(await listProducts(app_id));
    } catch {
      message.error(t("products.network"));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app_id]);

  const fail = (err: unknown) => {
    if (err instanceof ProductApiError && err.code === ProductApiCode.Limit) {
      message.error(t("products.limitTip"));
    } else {
      message.error(t("products.network"));
    }
  };

  const confirmDialog = (dialog: ConfirmDialog, run: () => Promise<void>) => {
    const body = dialog.bodyParams
      ? t(dialog.bodyKey, dialog.bodyParams)
      : t(dialog.bodyKey);
    const extra = dialog.unlistAt
      ? `\n${t("products.confirmUnlistLine", { time: dialog.unlistAt })}`
      : "";
    modal.confirm({
      title: t(dialog.titleKey),
      content: `${body}${extra}`,
      okText: t(dialog.okKey),
      cancelText: t(dialog.cancelKey ?? ConfirmCancel.Cancel),
      ...brandModalConfirmProps(dialog.danger ? "danger" : "primary"),
      onOk: async () => {
        try {
          await run();
          message.success(
            dialog.successParams
              ? t(dialog.successKey, dialog.successParams)
              : t(dialog.successKey),
          );
          await load();
        } catch (err) {
          fail(err);
          throw err;
        }
      },
    });
  };

  const run = (p: SubscriptionProduct, action: ListAction) => {
    const cmd = resolveListAction(action, p);
    if (cmd.type === ListCommandType.Edit) {
      setEditor({ kind: "edit", id: p.id });
      setEditorOpen(true);
      return;
    }
    if (cmd.type === ListCommandType.Blocked) {
      message.error(t(PUBLISH_ISSUE_KEY[cmd.issue]));
      setEditor({ kind: "edit", id: p.id });
      setEditorOpen(true);
      return;
    }
    if (cmd.type === ListCommandType.Duplicate) {
      void duplicateProduct(app_id, p.id)
        .then(() => {
          message.success(t("products.duplicated"));
          return load();
        })
        .catch(fail);
      return;
    }
    confirmDialog(cmd.dialog, () =>
      RUN_API[cmd.api](app_id, p.id).then(() => undefined),
    );
  };

  const columns = (status: ProductStatus): ColumnsType<SubscriptionProduct> => [
    {
      title: t("products.colName"),
      dataIndex: "name",
      render: (_, p) => displayName(p, i18n.language),
    },
    {
      title: t("products.status"),
      dataIndex: "status",
      width: 220,
      render: (_, p) => (
        <div className="flex flex-col items-start gap-1">
          <Tag
            className={styles.tag}
            variant="filled"
            color={STATUS_COLOR[p.status]}
          >
            {t(PRODUCT_STATUS_I18N[p.status])}
          </Tag>
          {p.status === ProductStatus.Scheduled && p.list_at && (
            <span className="text-xs text-tertiary">
              {t("products.listAt", { time: utcWall(p.list_at) })}
            </span>
          )}
          {p.status === ProductStatus.Published && p.unlist_at && (
            <span className="text-xs text-tertiary">
              {t("products.unlistAt", { time: utcWall(p.unlist_at) })}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t("products.colBilling"),
      dataIndex: "billing_type",
      render: (type: BillingType) => t(BILLING_I18N[type]),
    },
    {
      title: t("products.periods"),
      render: (_, p) =>
        periodSummary(
          p,
          (length, unit, many) =>
            t(periodPhraseKey(length, unit, many ? "short" : "head"), {
              n: length,
            }),
          t("products.dash"),
        ),
    },
    {
      title: t("products.actions"),
      width: 280,
      render: (_, p) => (
        <div className="flex flex-wrap gap-2">
          {actionsForStatus(status).map((key) => {
            const disabled = key === ListAction.Duplicate && atLimit;
            const btn = (
              <Button.Text
                key={key}
                type={key === ListAction.Delete ? "danger" : "primary"}
                className={`px-0 text-accent ${ACTION_CLASS[key] ?? ""}`}
                disabled={disabled}
                onClick={() => run(p, key)}
              >
                {t(LIST_ACTION_LABEL[key])}
              </Button.Text>
            );
            return disabled ? (
              <Tooltip key={key} title={t("products.limitTip")}>
                {btn}
              </Tooltip>
            ) : (
              btn
            );
          })}
        </div>
      ),
    },
  ];

  const groups = useMemo(() => groupProducts(rows), [rows]);

  const createBtn = atLimit ? (
    <Tooltip title={t("products.limitTip")}>
      <span>
        <BrandButton disabled icon={<PlusOutlined />}>
          {t("products.create")}
        </BrandButton>
      </span>
    </Tooltip>
  ) : (
    <BrandButton
      icon={<PlusOutlined />}
      onClick={() => {
        setEditor({ kind: "create" });
        setEditorOpen(true);
      }}
    >
      {t("products.create")}
    </BrandButton>
  );

  const renderGroup = (status: ProductStatus, items: SubscriptionProduct[]) => {
    const title = `${t(PRODUCT_STATUS_I18N[status])} (${items.length})`;
    const table = (
      <Table
        rowKey="id"
        size="middle"
        pagination={false}
        showSorterTooltip={false}
        columns={columns(status)}
        dataSource={items}
      />
    );
    return (
      <Collapse
        key={status}
        defaultActiveKey={status === ProductStatus.Unlisted ? [] : [status]}
        expandIcon={({ isActive }) => (
          <div className="flex items-center justify-center w-6 h-6 rounded-md border border-line">
            <ArrowCollapseIcon
              className={isActive ? "rotate-0" : "rotate-180"}
            />
          </div>
        )}
        items={[
          {
            key: status,
            label: (
              <span className="font-medium text-sm text-primary">{title}</span>
            ),
            children: table,
          },
        ]}
        className={styles.collapse}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4 bg-white p-6">
      <div>{createBtn}</div>

      {loading ? (
        <div className="rounded-lg bg-white py-16 text-center text-secondary">
          {t("common.loading")}
        </div>
      ) : !rows.length ? (
        <div className="rounded-lg bg-white py-16">
          <Empty description={t("products.empty")}>{createBtn}</Empty>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((g) => renderGroup(g.status, g.items))}
        </div>
      )}

      <ProductEdit
        key={
          editor == null
            ? "idle"
            : editor.kind === "edit"
              ? editor.id
              : "new"
        }
        open={editorOpen}
        creating={editor?.kind !== "edit"}
        productId={editor?.kind === "edit" ? editor.id : undefined}
        onClose={() => setEditorOpen(false)}
        onClosed={() => setEditor(null)}
        onChanged={() => void load(true)}
      />
    </div>
  );
}

const STATUS_COLOR: Record<ProductStatus, string> = {
  [ProductStatus.Published]: "success",
  [ProductStatus.Scheduled]: "processing",
  [ProductStatus.Draft]: "default",
  [ProductStatus.Unlisted]: "default",
};

const ACTION_CLASS: Partial<Record<ListAction, string>> = {
  [ListAction.ListNow]: "!text-[#f77234]",
  [ListAction.Cancel]: "!text-[#f77234]",
};

export default Products;
